import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import {
  authGuard,
  guestGuard,
  emailVerifiedGuard,
  blumeAdminGuard,
  partnerAdminGuard,
  partnerMemberGuard,
  profileGuard,
  licenseGuardFor,
  dataOptInGuard,
  goalDisabilityGuard,
  v3GovernanceGuard,
  v3GovernanceResolveGuard,
  v3CompatChildGuard,
  V3_COMPAT_SHELL_LANDING,
  confirmExitGuard,
} from './core/guards';
import { emailNotVerifiedGuard } from './core/guards/email-verified.guard';
import { AccountState } from './store/account/account.state';

export const routes: Routes = [
  // Auth Routes
  {
    path: 'sign-in',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/sign-in/sign-in.page').then(m => m.SignInPage),
  },
  {
    path: 'sign-up',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/sign-up/sign-up.page').then(m => m.SignUpPage),
  },
  {
    path: 'auth-actions',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/auth-actions/auth-actions.page').then(m => m.AuthActionsPage),
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage),
  },
  {
    path: 'setup-profile',
    canActivate: [authGuard],
    loadComponent: () => import('./features/community/setup-profile/setup-profile.page').then(m => m.SetupProfilePage),
  },
  {
    path: 'loading',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/user-initialization/user-initialization.page').then(m => m.UserInitializationPage),
  },
  {
    path: 'sign-out',
    loadComponent: () => import('./features/auth/sign-out/sign-out.page').then(m => m.SignOutPage),
  },
  // Open route — linked from the Google Play Store listing per Google's
  // account-deletion policy. No auth guard: visitors reaching this page
  // from the Play Store may not have the app installed or an account.
  {
    path: 'delete-account',
    loadComponent: () => import('./features/auth/delete-account/delete-account.page').then(m => m.DeleteAccountPage),
  },

  // Account Routes
  {
    path: 'account',
    pathMatch: 'full',
    redirectTo: 'my-blume',
  },
  {
    path: 'account/verify-email',
    pathMatch: 'full',
    canActivate: [authGuard, emailNotVerifiedGuard],
    loadComponent: () => import('./features/auth/verify-email/verify-email.page').then(m => m.VerifyEmailPage),
  },
  {
    path: 'account/enter-verification-code',
    pathMatch: 'full',
    canActivate: [authGuard, emailNotVerifiedGuard],
    loadComponent: () =>
      import('./features/auth/enter-verification-code/enter-verification-code.page').then(
        m => m.EnterVerificationCodePage,
      ),
  },
  {
    path: 'account/edit',
    pathMatch: 'full',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () => import('./features/auth/account-edit/account-edit.page').then(m => m.AccountEditPage),
  },
  {
    path: 'account/activity',
    pathMatch: 'full',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () =>
      import('./features/auth/account-activity/account-activity.page').then(m => m.AccountActivityPage),
  },
  {
    path: 'account/delete',
    pathMatch: 'full',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () => import('./features/auth/account-delete/account-delete.page').then(m => m.AccountDeletePage),
  },

  // Blume Admin
  {
    path: 'blume-admin',
    canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
    loadComponent: () => import('./features/blume-admin/blume-admin.page').then(m => m.BlumeAdminPage),
    loadChildren: () => import('./features/blume-admin/blume-admin.routes').then(m => m.BLUME_ADMIN_ROUTES),
  },

  // Blume Development
  {
    path: 'blume-development',
    canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
    loadComponent: () =>
      import('./features/blume-development/blume-development.page').then(m => m.BlumeDevelopmentPage),
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/blume-development/home/blume-development-home.page').then(m => m.BlumeDevelopmentHomePage),
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },

  // Admin standalone goal viewer — flat route that sits outside the admin
  // shell. Must precede the `admin/:partnerId` shell so `goals/:goalId`
  // isn't matched as a shell child.
  {
    path: 'admin/:partnerId/goals/:goalId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerAdminGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () => import('./features/notebook/goals/goal-view/goal-view.page').then(m => m.GoalViewPage),
  },

  // Bare `/admin` — redirect into the active partner's portal. Reached from
  // legacy links and the home/app-switcher cards (which set the active
  // partner before navigating here).
  {
    path: 'admin',
    pathMatch: 'full',
    redirectTo: () => {
      const accountState = inject(AccountState);
      const id = accountState.activeProfileId();
      return id ? `/admin/${id}/home` : '/my-blume';
    },
  },

  // Admin (partner-scoped shell with child routes). Compat-mode admins ENTER
  // the shell into a pared-down V2-workspace surface (Users + Insights): the
  // resolve guard only guarantees governance is loaded, and the child gate
  // enforces per-child compat dispositions — hidden children land on the
  // shell's own compat landing (the Users page) via V3_COMPAT_SHELL_LANDING.
  {
    path: 'admin/:partnerId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerAdminGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canActivateChild: [v3CompatChildGuard],
    data: { [V3_COMPAT_SHELL_LANDING]: '/admin/:partnerId/users' },
    loadComponent: () => import('./features/admin/admin.page').then(m => m.AdminPage),
    loadChildren: () => import('./features/admin/admin.routes').then(m => m.ADMIN_ROUTES),
  },

  // Academy
  {
    path: 'academy',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./features/academy/academy.page').then(m => m.AcademyPage),
    loadChildren: () => import('./features/academy/academy.routes').then(m => m.ACADEMY_ROUTES),
  },

  // UI Kit (internal only)
  {
    path: 'ui-kit',
    canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
    loadComponent: () => import('./features/ui-kit-showcase/ui-kit.page').then(m => m.UiKitPage),
    loadChildren: () => import('./features/ui-kit-showcase/ui-kit-showcase.routes').then(m => m.UI_KIT_SHOWCASE_ROUTES),
  },

  // Documentation (with profileGuard — full docs)
  {
    path: 'documentation',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./features/documentation/documentation.page').then(m => m.DocumentationPage),
    loadChildren: () => import('./features/documentation/documentation.routes').then(m => m.DOCUMENTATION_ROUTES),
  },

  // Community
  {
    path: 'my-blume',
    canActivate: [authGuard, emailVerifiedGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./features/community/home/home.page').then(m => m.HomePage),
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  {
    path: 'help',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () => import('./features/help-center/help-center.page').then(m => m.HelpCenterPage),
    loadChildren: () => import('./features/help-center/help-center.routes').then(m => m.HELP_CENTER_ROUTES),
  },

  // Notebook (flat routes that sit outside the notebook shell). Partner-scoped
  // via `:partnerId`; `partnerMemberGuard` authorizes membership + reconciles
  // the active partner to the URL. Must precede the `notebook/:partnerId` shell.
  {
    path: 'notebook/:partnerId/students/:studentId/goals/add',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () => import('./features/notebook/students/goal-add/goal-add.page').then(m => m.GoalAddPage),
  },
  {
    path: 'notebook/:partnerId/home/goals/create/iep-goal',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/goals/iep-goals/iep-goals-create/iep-goals-create.page').then(
        m => m.IepGoalsCreatePage,
      ),
  },
  // Goal Drafter is export-only under compat (86e2dfyvv): the resolve guard
  // admits compat users, and the page severs every persistence path. The
  // param-less entry is the compat path (free-text student reference).
  {
    path: 'notebook/:partnerId/goal-drafter/:studentId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
      goalDisabilityGuard,
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-drafter/goal-drafter.page').then(m => m.GoalDrafterPage),
  },
  {
    path: 'notebook/:partnerId/goal-drafter',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-drafter/goal-drafter.page').then(m => m.GoalDrafterPage),
  },
  {
    path: 'notebook/:partnerId/goal/:goalId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () => import('./features/notebook/goals/goal-view/goal-view.page').then(m => m.GoalViewPage),
  },
  // Data collection is export-only under compat (86e2dfyw8): resolve guard
  // admits compat users; the param-less entry is the compat path (free-text
  // references + static skill picker).
  {
    path: 'notebook/:partnerId/collect/goal/:goalId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-collect/goal-collect.page').then(m => m.GoalCollectPage),
  },
  {
    path: 'notebook/:partnerId/collect/goal',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-collect/goal-collect.page').then(m => m.GoalCollectPage),
  },
  {
    path: 'notebook/:partnerId/goal-builder/:studentId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/goals/goal-builder/goal-builder.page').then(m => m.GoalBuilderPage),
  },
  {
    path: 'notebook/:partnerId/collect',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/observation-input-form/observation-input-form.page').then(
        m => m.ObservationInputFormPage,
      ),
  },
  {
    path: 'notebook/:partnerId/collect/quick',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/observation-input-form/generic-observation-form.page').then(
        m => m.GenericObservationFormPage,
      ),
  },
  {
    path: 'notebook/:partnerId/home/collect/record/:studentId/:skillId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/observation/observation.page').then(m => m.ObservationPage),
  },
  {
    path: 'notebook/:partnerId/home/collect/record',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/observation/observation.page').then(m => m.ObservationPage),
  },
  {
    // Goal-less edit page — loads the observation (and any session siblings)
    // and renders the same form shell as `/notebook/:partnerId/collect/goal/:goalId`,
    // but in update-existing mode. Must precede `:observationId` so `edit`
    // isn't matched as an observationId.
    path: 'notebook/:partnerId/observations/:observationId/edit',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/observations/observation-edit/observation-edit.page').then(
        m => m.ObservationEditPage,
      ),
  },
  {
    path: 'notebook/:partnerId/observations/:observationId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/observation/observation.page').then(m => m.ObservationPage),
  },
  {
    path: 'notebook/:partnerId/home/collect/observation/:studentId/:skillId/:measurementTypes',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/manual-entry/manual-entry-form/manual-entry-form.page').then(
        m => m.ManualEntryFormPage,
      ),
  },
  {
    path: 'notebook/:partnerId/home/collect/observation/:studentId/:skillId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () =>
      import('./features/notebook/collect/manual-entry/manual-entry-measurements-select/manual-entry-measurements-select.page').then(
        m => m.ManualEntryMeasurementsSelectPage,
      ),
  },
  {
    path: 'notebook/:partnerId/home/goals/create/:studentId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () => import('./features/notebook/goals/create-goal/create-goal.page').then(m => m.CreateGoalPage),
  },
  {
    path: 'notebook/:partnerId/home/goals/create/',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceGuard,
      licenseGuardFor('tools'),
    ],
    loadComponent: () => import('./features/notebook/goals/create-goal/create-goal.page').then(m => m.CreateGoalPage),
  },

  // Compatibility aliases kept for the V3 guard's param-less COMPAT_LANDING
  // and old testing links. In-UI navigation uses partner-scoped accountState URLs.
  {
    path: 'notebook/goal-drafter',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-drafter/goal-drafter.page').then(m => m.GoalDrafterPage),
  },
  {
    path: 'notebook/collect/goal',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canDeactivate: [confirmExitGuard],
    loadComponent: () =>
      import('./features/notebook/goals/goal-collect/goal-collect.page').then(m => m.GoalCollectPage),
  },

  // Bare `/notebook` — redirect into the active partner's notebook. Reached
  // from legacy links and the home/app-switcher cards (which set the active
  // partner before navigating here).
  {
    path: 'notebook',
    pathMatch: 'full',
    redirectTo: () => {
      const accountState = inject(AccountState);
      const id = accountState.activeProfileId();
      return id ? `/notebook/${id}/caseload` : '/my-blume';
    },
  },

  // Notebook (partner-scoped shell with child routes). Compat-mode users ENTER
  // the shell (86e2dfp5m): the resolve guard only guarantees governance is
  // loaded, and the child gate enforces per-child compat dispositions — parent
  // guards do not re-run on child-to-child navigation.
  {
    path: 'notebook/:partnerId',
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      profileGuard,
      partnerMemberGuard,
      dataOptInGuard,
      v3GovernanceResolveGuard,
      licenseGuardFor('tools'),
    ],
    canActivateChild: [v3CompatChildGuard],
    loadComponent: () => import('./features/notebook/notebook.page').then(m => m.NotebookPage),
    loadChildren: () => import('./features/notebook/notebook.routes').then(m => m.NOTEBOOK_ROUTES),
  },

  // License-expired landing page — no license guard (would infinite-loop).
  // Reached when `licenseGuardFor(module)` denies access.
  {
    path: 'license-expired',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./features/auth/license-expired/license-expired.page').then(m => m.LicenseExpiredPage),
  },

  // Wildcard fallback
  {
    path: '**',
    redirectTo: 'my-blume',
  },
];
